<template>
  <div class="review-step">
    <!-- Item que não é vendido avulso (aula exclusiva de plano): quem diz se
         ele está disponível é o pai, que conhece a regra do próprio tipo. -->
    <slot name="status"/>

    <!-- Estado atual -->
    <b-alert :show="!noSale && published" variant="success" class="p-1 mb-2">
      <div class="flex items-center flex-wrap gap-2">
        <feather-icon icon="CheckCircleIcon" class="flex-shrink-0"/>
        <span>{{ $t('catalog.review.published_banner') }}</span>
        <a
          v-if="frontUrl"
          :href="frontUrl"
          target="_blank"
          rel="noopener"
          class="review-step-link">
          {{ $t('catalog.review.see_listing') }}
          <feather-icon icon="ExternalLinkIcon" svgClasses="w-3 h-3"/>
        </a>
      </div>
    </b-alert>
    <b-alert :show="!noSale && !published" variant="info" class="p-1 mb-2">
      <div class="flex items-center gap-2">
        <feather-icon icon="EyeOffIcon" class="flex-shrink-0"/>
        <span>{{ $t('catalog.review.not_published_banner') }}</span>
      </div>
    </b-alert>

    <!-- Resumo -->
    <div class="review-step-grid">
      <div class="review-step-card" v-for="section in summary" :key="section.key">
        <div class="review-step-card-head">
          <h6 class="mb-0">
            <feather-icon v-if="section.icon" :icon="section.icon" class="mr-50" svgClasses="w-4 h-4"/>
            {{ section.title }}
          </h6>
          <b-button
            v-if="section.stepKey"
            variant="flat-primary"
            size="sm"
            @click="$emit('goto', section.stepKey)">
            {{ $t('catalog.review.edit') }}
          </b-button>
        </div>
        <dl class="review-step-rows">
          <template v-for="(row, index) in section.rows">
            <dt :key="`t-${index}`">{{ row.label }}</dt>
            <dd :key="`d-${index}`" :class="{ 'text-warning': row.missing }">
              <span
                :ref="rowKey(section.key, index)"
                class="review-step-value"
                :class="{ 'review-step-value-clamped': !isRowExpanded(section.key, index) }">{{ formatRowValue(row) }}</span>
              <button
                v-if="isRowOverflowing(section.key, index)"
                type="button"
                class="review-step-toggle"
                @click="toggleRow(section.key, index)">
                {{ isRowExpanded(section.key, index) ? $t('catalog.review.see_less') : $t('catalog.review.see_more') }}
              </button>
            </dd>
          </template>
        </dl>
      </div>

      <!-- Preço em destaque -->
      <div class="review-step-card review-step-price-card" v-if="!noSale">
        <div class="review-step-card-head">
          <h6 class="mb-0">
            <feather-icon icon="DollarSignIcon" class="mr-50" svgClasses="w-4 h-4"/>
            {{ $t('catalog.review.price') }}
          </h6>
          <b-button variant="flat-primary" size="sm" @click="$emit('goto', priceStepKey)">
            {{ $t('catalog.review.edit') }}
          </b-button>
        </div>
        <div class="review-step-price">
          <span class="review-step-price-value">{{ priceLabel }}</span>
          <small v-if="priceSuffix" class="review-step-price-suffix">{{ priceSuffix }}</small>
        </div>
        <small v-if="isFree" class="text-warning">{{ $t('catalog.sale.free_notice') }}</small>
      </div>
    </div>

    <!-- Pendências -->
    <b-alert :show="blockers.length > 0" variant="warning" class="p-1 mt-2 mb-0">
      <div class="mb-50 font-weight-bold">{{ $t('catalog.review.blockers_title') }}</div>
      <ul class="mb-0 pl-2">
        <li v-for="(blocker, index) in blockers" :key="index">
          <a v-if="blocker.stepKey" href="#" @click.prevent="$emit('goto', blocker.stepKey)">{{ blocker.text }}</a>
          <template v-else>{{ blocker.text }}</template>
        </li>
      </ul>
    </b-alert>

    <!-- Ações -->
    <div class="review-step-actions">
      <!-- Sem venda avulsa não há o que publicar: o item já vale para quem tem
           plano assim que está cadastrado. Só resta salvar e sair. -->
      <template v-if="noSale">
        <b-button
          variant="primary"
          size="lg"
          :disabled="busy || blockers.length > 0"
          @click="$emit('finish')">
          <feather-icon icon="CheckIcon" class="mr-50"/>
          <span class="align-middle">{{ $t('catalog.review.finish') }}</span>
        </b-button>
      </template>
      <template v-else-if="!published">
        <b-button
          variant="primary"
          size="lg"
          :disabled="busy || blockers.length > 0"
          @click="$emit('publish')">
          <feather-icon icon="GlobeIcon" class="mr-50"/>
          <span class="align-middle">{{ $t('catalog.review.publish_now') }}</span>
        </b-button>
        <b-button
          variant="outline-secondary"
          :disabled="busy"
          @click="$emit('save-draft')">
          {{ $t('catalog.review.save_draft') }}
        </b-button>
      </template>
      <template v-else>
        <b-button
          variant="primary"
          :disabled="busy || blockers.length > 0"
          @click="$emit('publish')">
          {{ $t('catalog.review.save_changes') }}
        </b-button>
        <b-button
          variant="outline-danger"
          :disabled="busy"
          @click="confirmUnpublish">
          <feather-icon icon="EyeOffIcon" class="mr-50"/>
          <span class="align-middle">{{ $t('catalog.review.unpublish') }}</span>
        </b-button>
      </template>
    </div>
    <small class="text-muted d-block mt-1" v-if="!noSale">
      {{ published ? $t('catalog.review.publish_saves_all') : $t('catalog.review.draft_explainer') }}
    </small>
  </div>
</template>

<script>
import { BAlert, BButton } from 'bootstrap-vue'
import { formatPrice } from './catalog'

/**
 * Passo final: resumo do que foi cadastrado + decisão explícita de publicação.
 *
 * A decisão foi de projeto (2026-08-15): nada é publicado por efeito colateral.
 * O usuário vê o resumo, vê as pendências que impedem a publicação (cada uma
 * levando ao passo que resolve) e escolhe entre "Publicar agora" e "Salvar
 * como rascunho". Despublicar grava status DI — o anúncio some da vitrine mas
 * nada é apagado.
 */
export default {
  name: 'ReviewStep',
  components: { BAlert, BButton },
  props: {
    /** [{ key, title, icon?, stepKey?, rows: [{label, value, missing?}] }] */
    summary: { type: Array, default: () => [] },
    price: { type: [Number, String], default: null },
    priceSuffix: { type: String, default: null },
    priceStepKey: { type: String, default: 'venda' },
    published: { type: Boolean, default: false },
    blockers: { type: Array, default: () => [] },
    frontUrl: { type: String, default: null },
    busy: { type: Boolean, default: false },
    /** Item sem venda avulsa (aula exclusiva de plano): sem preço nem publicação. */
    noSale: { type: Boolean, default: false },
  },
  data() {
    return {
      // Chave `${section.key}-${index}` -> boolean. Só guarda o que já foi
      // medido/expandido; linha nova entra como "não estourou" até o próximo checkOverflow.
      expandedRows: {},
      overflowingRows: {},
    }
  },
  computed: {
    priceLabel() {
      const formatted = formatPrice(this.price)
      return formatted === null ? '—' : formatted
    },
    isFree() {
      return Number(this.price) === 0 && this.price !== null && this.price !== ''
    },
  },
  mounted() {
    this.$nextTick(this.checkOverflow)
    window.addEventListener('resize', this.handleResize)
  },
  updated() {
    this.$nextTick(this.checkOverflow)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    clearTimeout(this.resizeTimer)
  },
  methods: {
    rowKey(sectionKey, index) {
      return `value-${sectionKey}-${index}`
    },
    formatRowValue(row) {
      return row.value === null || row.value === undefined || row.value === '' ? '—' : row.value
    },
    isRowExpanded(sectionKey, index) {
      return !!this.expandedRows[this.rowKey(sectionKey, index)]
    },
    isRowOverflowing(sectionKey, index) {
      return !!this.overflowingRows[this.rowKey(sectionKey, index)]
    },
    toggleRow(sectionKey, index) {
      const key = this.rowKey(sectionKey, index)
      this.$set(this.expandedRows, key, !this.expandedRows[key])
    },
    handleResize() {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(this.checkOverflow, 150)
    },
    // Só mede linhas recolhidas: expandida, o span mostra o texto inteiro e
    // scrollWidth==clientWidth sempre, o que apagaria o botão "Ver menos".
    checkOverflow() {
      this.summary.forEach((section) => {
        (section.rows || []).forEach((row, index) => {
          const key = this.rowKey(section.key, index)
          if (this.expandedRows[key]) return
          const el = this.$refs[key]
          if (!el) return
          const overflowing = el.scrollWidth - el.clientWidth > 1
          if (overflowing !== !!this.overflowingRows[key]) {
            this.$set(this.overflowingRows, key, overflowing)
          }
        })
      })
    },
    confirmUnpublish() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'warning',
        title: 'Confirmação',
        acceptText: this.$t('catalog.review.unpublish'),
        cancelText: 'Cancelar',
        text: this.$t('catalog.review.unpublish_confirm'),
        accept: () => this.$emit('unpublish'),
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '/src/@core/scss/base/bootstrap-extended/_variables.scss';

.review-step-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.75rem;
}

.review-step-card {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.6rem;
  padding: 0.9rem 1rem;
}

.review-step-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;

  h6 {
    display: flex;
    align-items: center;
    font-weight: 600;
  }
}

.review-step-rows {
  display: grid;
  // A label ("dt") não pode dominar a linha: sem o teto em 45%, um rótulo
  // longo ("Até quantos dias...") vira max-content e espreme o valor a quase
  // nada. `minmax(0, 1fr)` no valor é o que permite o ellipsis funcionar
  // dentro do grid — sem o 0 o track herda o min-content do texto e nunca encolhe.
  grid-template-columns: minmax(90px, 45%) minmax(0, 1fr);
  column-gap: 0.75rem;
  row-gap: 0.25rem;
  margin: 0;

  dt {
    font-weight: 500;
    opacity: 0.65;
    font-size: 0.85rem;
  }

  dd {
    margin: 0;
    font-size: 0.9rem;
    min-width: 0;
  }
}

.review-step-value {
  display: block;
  overflow-wrap: break-word;
  word-break: break-word;
}

.review-step-value-clamped {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.review-step-toggle {
  display: inline-block;
  margin-top: 0.1rem;
  padding: 0;
  border: 0;
  background: none;
  color: $primary;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;

  &:hover,
  &:focus {
    text-decoration: underline;
    outline: none;
  }
}

.review-step-price {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
}

.review-step-price-value {
  font-size: 1.6rem;
  font-weight: 700;
}

.review-step-price-suffix {
  opacity: 0.65;
}

.review-step-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
  flex-wrap: wrap;
}

.review-step-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  text-decoration: underline;
}
</style>
